/** @format */

import {useCallback, useEffect, useRef, useState} from 'react';

/**
 * useRequest is a hook for asynchronously fetching data when a dependencies array
 * changes. It is a DIY version of useSWR and react-query and the ilk, with an added
 * debouncing feature.
 *
 * @param {Array} deps An array of dependencies that, when changed, will cause the request
 * to fire. If null, the request will not fire.
 * @param {Function} fetcher A function that returns a promise which resolves to some
 * data.
 * @param {Object} options
 * @param {number} options.debounce Milliseconds to delay firing the request.
 * @param {number} options.initial Initial value of `data` before a result
 * @returns {{data: *, isPending: Boolean, error: Object}}
 */
export default function useRequest(deps, fetcher, options = {debounce: 500, initial: {}}) {
    const [data, setData] = useState(options.initial);
    const [isPending, setIsPending] = useState(false);
    const [error, setError] = useState();
    const abortableReq = useRef(null);

    // The dependencies of the useEffect below are a debounced version of the deps
    // argument, so that the useEffect only triggers after the debounce period. The deps
    // are JSON stringified to help keep them stable between renders.
    const debouncedDeps = useDebounce(JSON.stringify(deps), options.debounce);

    // This function passed to the fetcher, so that the caller can provide useRequest with a
    // request object that is abortable (i.e. SuperAgent.get/post). abort() will be called
    // on that object if another request is triggered while it's still running.
    const setAbortable = v => (abortableReq.current = v);

    const doRequest = useCallback(async () => {
        // Abort the previous request, if available.
        abortableReq.current?.abort();
        abortableReq.current = null;

        setIsPending(true);

        try {
            const data = await fetcher({abortable: setAbortable});
            setData(data);
            setError(null);
            setIsPending(false);
            abortableReq.current = null;
        } catch (err) {
            if (err.message === 'Aborted') {
                return;
            } else {
                // console.log(err);
                setData(null);
                setError(err);
                setIsPending(false);
                abortableReq.current = null;
            }
        }
    }, [fetcher, debouncedDeps]);

    useEffect(() => {
        debouncedDeps !== 'null' && doRequest();
    }, [debouncedDeps]);

    return {data, error, isPending, request: doRequest};
}

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    const isInitial = useRef(true);

    useEffect(() => {
        // No need to debounce the value on the first set.
        if (isInitial.current) {
            isInitial.current = false;
            return;
        }

        const handler = setTimeout(() => {
            setDebouncedValue(value);
        }, delay);

        return () => {
            clearTimeout(handler);
        };
    }, [value]);

    return debouncedValue;
}
